import {
  useCreateAndUploadEmeraMediaForMetaMutation,
  useCreateAndUploadOverlayFromPropertyIdForMobileMutation,
  useUploadImageMutation,
} from '@/editor/services/http/media-library.api';
import { EDITOR_LS_CONSTANTS } from '@/editor/shared/constants/editor-LS.constants';
import { setImageOverlayId, setOriginalOverlayImage } from '@/editor/store/reducers/creative-step.slice';
import { getValueFromLs, setKeyValuePairToLS } from '@/shared/services/localstorage';
import { useAppDispatch, useAppSelector } from '@/store/hooks/redux';
import Close from '@mui/icons-material/Close';
import CloudUpload from '@mui/icons-material/CloudUpload';
import Image from '@mui/icons-material/Image';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';

import type { FC, JSX } from 'react';
import { TemplateType } from '@/editor/shared/constants/template-type.enum';
import { AvailablePlatformsEnum } from '@/editor/shared/constants/available-platforms';
import type { IMediaLibraryImageModel } from '@/shared/models/media-library/media-library-image.model';
import type { CreativeType } from '@/editor/shared/constants/creatives-types.enum';
import { ExternalCompanyIdEnum } from '@/shared/constants/external-company-id.enum';

export interface ConfirmImageDialogProps {
  open: boolean;
  imageUrl: string;
  imageIsLoading: boolean;
  onClose: (confirm: boolean) => void;
  originalImage: IMediaLibraryImageModel | null;
  item?: IMediaLibraryImageModel;
}

const ConfirmImageDialog: FC<ConfirmImageDialogProps> = ({
  open,
  imageUrl,
  imageIsLoading,
  onClose,
  originalImage,
  item,
}): JSX.Element => {
  const [uploadImage, { isLoading, isError, isSuccess }] = useUploadImageMutation();
  const dispatch = useAppDispatch();
  const drawerState = useAppSelector((state) => state.editorModalsDrawersReducer.mediaLibraryDrawerState);
  const creativeFormsState = useAppSelector((state) => state.creativeStepSliceReducer.creativeFormsState);
  const { t } = useTranslation();
  const data = creativeFormsState[AvailablePlatformsEnum.META][drawerState.creativeType as CreativeType];
  const propertyId = getValueFromLs(EDITOR_LS_CONSTANTS.PROPERTY_INTERNAL_ID)
    ? getValueFromLs(EDITOR_LS_CONSTANTS.PROPERTY_INTERNAL_ID)
    : useAppSelector((state) => state.propertyInfoSliceReducer.propertyInternalId);
  const isDefaultOverlay = useAppSelector((state) => state.creativeStepSliceReducer.isOverlayDefault);
  const companyId = useAppSelector((state) => state.propertyInfoSliceReducer.companyId);

  const handleClose = (confirm: boolean) => {
    onClose(confirm);
  };
  const [createImageForMetaEmera, { isLoading: metaEmeraFetching }] = useCreateAndUploadEmeraMediaForMetaMutation();
  const [createOverlayFromPropertyIdForMobile, { isLoading: overlayFetching }] =
    useCreateAndUploadOverlayFromPropertyIdForMobileMutation();

  const handleConfirm = async () => {
    const img = new window.Image();
    img.src = imageUrl;

    if (!originalImage) {
      console.error(`Original image not selected`);
      return;
    }
    if (!isDefaultOverlay) {
      await createImageForMetaEmera({
        propertyFileId: item!.id,
        payload: {
          withSave: true,
          isMobile: true,
          localAreaName: data?.localAreaName!,
          propertyInfo: data?.propertyInfoMobile!,
          desc: data?.description!,
          brokerName: data?.employeeName!,
          brokerTitle: data?.employeeTitle!,
        },
      })
        .then((res) => {
          setKeyValuePairToLS(EDITOR_LS_CONSTANTS.IMAGE_ID, res.data?.propertyFileImageId!);
        })
        .catch((error) => {
          console.error('Error creating image for Meta Emera:', error);
        });
    } else if (
      isDefaultOverlay &&
      companyId == ExternalCompanyIdEnum.EMERA_EIE &&
      drawerState.platform == AvailablePlatformsEnum.META
    ) {
      await createOverlayFromPropertyIdForMobile({
        id: item!.id,
        internalPropertyId: propertyId as string,
      })
        .then((res) => {
          setKeyValuePairToLS(EDITOR_LS_CONSTANTS.IMAGE_ID, res.data?.propertyFileImageId!);
        })
        .catch((error) => {
          console.error('Error creating image for Meta Emera overlay:', error);
        });
    }
    fetch(imageUrl)
      .then((r) => r.blob())
      .then((res) => {
        uploadImage({
          extension: 'jpg',
          width: img.width,
          height: img.height,
          propertyId: propertyId as string,
          file: res as File,
        })
          .unwrap()
          .then((res) => {
            dispatch(setImageOverlayId({ id: res.propertyFileId }));
            dispatch(setOriginalOverlayImage({ image: originalImage }));
            if (drawerState.platform == AvailablePlatformsEnum.BIDTHEATRE) {
              setKeyValuePairToLS(EDITOR_LS_CONSTANTS.TEMPLATE_TYPE, TemplateType.AFTERMAEKETING_TEMPLATE);
            }
            handleClose(true);
          });
      });
  };

  return (
    <Dialog onClose={handleClose} open={open}>
      <Grid
        container
        p={2}
        sx={{
          minWidth: '560px',
          minHeight: '460px',
        }}>
        {imageIsLoading ? (
          <Grid item xs={12} display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <Box sx={{ position: 'relative' }}>
              <CircularProgress
                variant="indeterminate"
                size={40}
                sx={{
                  color: '#000',
                  position: 'absolute',
                  top: -7,
                  left: -8,
                  zIndex: 1,
                }}
              />
              <Image sx={{ fontSize: 25 }} color="secondary" />
            </Box>
          </Grid>
        ) : (
          <>
            <Grid item xs={12}>
              <img src={imageUrl} width={'100%'} />
            </Grid>
            {isLoading || metaEmeraFetching || overlayFetching ? (
              <Grid item xs={12} my={1} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <Box sx={{ position: 'relative' }}>
                  <CircularProgress
                    variant="indeterminate"
                    size={40}
                    sx={{
                      color: '#000',
                      position: 'absolute',
                      top: -7,
                      left: -8,
                      zIndex: 1,
                    }}
                  />
                  <UploadFileRounded sx={{ fontSize: 25 }} color="secondary" />
                </Box>
              </Grid>
            ) : (
              <Grid item xs={12} mt={2} display={'flex'} justifyContent={'space-between'} alignItems={'flex-end'}>
                <Button onClick={() => handleClose(false)} variant="contained" color={'error'} startIcon={<Close />}>
                  {t('cancel')}
                </Button>
                <Button onClick={handleConfirm} variant="contained" color={'secondary'} startIcon={<CloudUpload />}>
                  {t('create')}
                </Button>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Dialog>
  );
};

export default ConfirmImageDialog;
